import React from 'react';
import styled from 'astroturf/react';
import { ButtonLink } from 'js/design_system/link/ButtonLink';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import routes from 'js/core_pre_webpack/routes';
import { useInBreakpoints } from 'js/design_system/hooks/useBreakpoint';

const PodcastLibraryIcon: React.FC<{ className?: string }> = ({
  className,
}) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_4977_23820"
      style={{ maskType: 'alpha' }}
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect width="24" height="24" fill="#D9D9D9" />
    </mask>
    <g mask="url(#mask0_4977_23820)">
      <path
        d="M4 22C3.71667 22 3.47917 21.9042 3.2875 21.7125C3.09583 21.5208 3 21.2833 3 21V7.05C3 6.8 3.05 6.5875 3.15 6.4125C3.25 6.2375 3.41667 6.1 3.65 6L13.65 2C13.9833 1.86667 14.2917 1.9125 14.575 2.1375C14.8583 2.3625 15 2.65 15 3V6H16C16.2833 6 16.5208 6.09583 16.7125 6.2875C16.9042 6.47917 17 6.71667 17 7V10H15V8H5V20H10.175L12.175 22H4ZM9 6H13V4.45L9 6ZM17 22C15.6167 22 14.4375 21.5125 13.4625 20.5375C12.4875 19.5625 12 18.3833 12 17C12 15.6167 12.4875 14.4375 13.4625 13.4625C14.4375 12.4875 15.6167 12 17 12C18.3833 12 19.5625 12.4875 20.5375 13.4625C21.5125 14.4375 22 15.6167 22 17C22 18.3833 21.5125 19.5625 20.5375 20.5375C19.5625 21.5125 18.3833 22 17 22ZM15.75 19.5L19.75 17L15.75 14.5V19.5Z"
        fill="white"
      />
    </g>
  </svg>
);

const Banner = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100px;
  background-color: #0f373a;
  border-radius: var(--border-radius-radius-md);
  padding: var(--spacing-sm-24, 24px);
  margin-bottom: -24px;
  gap: var(--spacing-xs-16, 16px);

  @include breakpoint(small) {
    flex-direction: column;
    height: auto;
    padding: var(--spacing-xs-16, 16px);
    gap: var(--spacing-xs-12, 12px);
    align-items: flex-start;
  }
`;

const Content = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-8, 8px);

  @include breakpoint(small) {
    width: 100%;
  }
`;

const HeadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-8, 8px);
`;

const Icon = styled(PodcastLibraryIcon)`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
`;

const Heading = styled.div`
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: 20px;
  line-height: 32px;
  color: var(--text-white, #ffffff);
  margin: 0;
`;

const Subtext = styled.p`
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  color: var(--text-white, #ffffff);
  margin: 0;
`;

const ButtonContainer = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  flex-shrink: 0;

  @include breakpoint(small) {
    width: 100%;
  }
`;

export const PodcastLibraryProfileBanner: React.FC = () => {
  const isMobile = useInBreakpoints(['small', 'medium']);

  return (
    <ImpressionWatcher source="podcast_lib.profile.banner">
      <Banner>
        <Content>
          <HeadingRow>
            <Icon />
            <Heading>Discover podcast library</Heading>
          </HeadingRow>
          <Subtext>Listen to paper insights in minutes, not hours</Subtext>
        </Content>
        <ButtonContainer>
          <ImpressionWatcher source="podcast_lib.profile.banner-browse-button">
            <ButtonLink
              href={routes.podcast_library_url()}
              size="xs"
              iconRight="arrow_forward"
              fullWidth={isMobile}
            >
              Browse podcast library
            </ButtonLink>
          </ImpressionWatcher>
        </ButtonContainer>
      </Banner>
    </ImpressionWatcher>
  );
};
